import { Component, Input, OnInit } from '@angular/core';
import { DownloadListModalOptions } from '@app/lists/model/download-list-options.model';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneBasicSelection,
  dualPaneDefaultVerifySave,
  getSearchableObservable$,
  handleInputOrRangeValueSave,
  singleSelectRadioSearchDefault,
  trimSpaceTo60,
  updateRightPaneWithValue,
  updateRightPaneWithValueForMaps,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import {
  DualPaneView,
  MultiInputItems,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import { ListTypes } from '@shared/constants/lists-constants';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  InputLabelItems,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import { DownloadListOptions } from '@usf/ngrx-list';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';

@Component({
  selector: 'app-list-download-container',
  templateUrl: './list-download-container.component.html',
  styleUrls: ['./list-download-container.component.scss'],
})
export class ListDownloadContainerComponent extends BaseDualPaneModalContainerComponent {
  @Input() translateService: TranslateService;
  @Input() hidePricingOption: boolean;

  listName: string;
  listId: number;
  listTypeId: string;
  isDownload: boolean;
  downloadOptions = [];
  downloadFormats = [];
  nutritionFlag = false;

  constructor(
    public platformService: PlatformService,
    readonly panAppState: PanAppState,
  ) {
    super(platformService);
  }

  createListDownloadVM = () => {
    this.setupDownloadFormats();
    this.setupDownloadOptions();
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'list-download-modal',
      title: this.translateService.instant(
        'i18n.lists.downloadListModal.title',
        { listName: this.listName },
      ),
      htmlSubTitle: this.translateService.instant(
        'i18n.lists.downloadListModal.downloadStatement',
      ),
      submitText: this.translateService.instant('i18n.common.download'),
      backText: this.translateService.instant('i18n.common.save'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildNewNameRightPane,
      handleSubmit: this.submitModalValues,
      verifySubmit: this.listDownloadVerify,
    };

    const flagConfig: DualPaneFlagsConfig = {};
    const nameMap = new Map<number, any>();
    nameMap.set(0, this.listName);
    const contentItems = [
      {
        label: 'i18n.lists.downloadListModal.fileName',
        placeHolder: 'i18n.lists.downloadListModal.chooseName',
        view: DualPaneView.multiInput,
        selected: this.platformService.platformType !== PlatformEnum.mobile,
        value: nameMap,
        subLabel: this.listName,
        onSelectfunction: this.buildNewNameRightPane,
      },
      {
        label: 'i18n.lists.downloadListModal.fileFormat',
        placeHolder: 'i18n.lists.downloadListModal.chooseType',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        value: 'CSV',
        subLabel: 'CSV',
        onSelectfunction: this.buildListFormatsRightPane,
      },
      {
        label: 'i18n.lists.downloadListModal.downloadOptions',
        placeHolder: 'i18n.lists.downloadListModal.chooseOptions',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        onSelectfunction: this.buildListOptions,
      },
    ] as BasicListItem[];

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
  };

  setupDownloadFormats = () => {
    this.downloadFormats = [
      {
        label: 'i18n.lists.downloadListModal.csv',
        value: 'CSV',
        selected: true,
        type: MultiSelectItems.basic,
      },
      {
        label: 'i18n.lists.downloadListModal.xlsx',
        value: 'XLSX',
        selected: false,
        type: MultiSelectItems.basic,
      },
      {
        label: 'i18n.lists.downloadListModal.xml',
        value: 'XML',
        selected: false,
        type: MultiSelectItems.basic,
      },
      {
        label: 'i18n.lists.downloadListModal.pdf',
        value: 'PDF',
        selected: true,
        type: MultiSelectItems.basic,
      },
      {
        label: 'i18n.lists.downloadListModal.price',
        value: 'Price File',
        selected: false,
        type: MultiSelectItems.basic,
      },
      {
        label: 'i18n.lists.downloadListModal.priceAcii',
        value: 'Price File ASCII',
        selected: false,
        type: MultiSelectItems.basic,
      },
    ];
  };

  setupDownloadOptions = () => {
    this.downloadOptions = [];

    if (!this.hidePricingOption) {
      this.downloadOptions.push({
        label: 'i18n.lists.downloadListModal.includePrice',
        value: 'price',
        selected: false,
        type: MultiSelectItems.basic,
      });
    }

    if (this.listTypeId !== ListTypes.recentlyPurchased) {
      this.downloadOptions.push({
        label: 'i18n.lists.downloadListModal.includeNote',
        value: 'note',
        selected: false,
        type: MultiSelectItems.basic,
      });
    }
    this.downloadOptions.push({
      label: 'i18n.lists.downloadListModal.includeStatus',
      value: 'status',
      selected: false,
      type: MultiSelectItems.basic,
    });

    if (this.nutritionFlag) {
      this.downloadOptions.push({
        label: 'i18n.lists.downloadListModal.includeNutrition',
        value: 'nutrition',
        selected: false,
        type: MultiSelectItems.basic,
      });
    }
    this.downloadOptions.push({
      label: 'i18n.lists.downloadListModal.masterListAssociation',
      value: 'masterListAssociation',
      selected: false,
      type: MultiSelectItems.basic,
    });
  };

  buildNewNameRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let inputValue = undefined;
    if (!!viewModel?.leftView?.content[0].value) {
      inputValue = viewModel?.leftView?.content[0].value.get(0);
    } else if (!viewModel?.leftView) {
      inputValue = this.listName;
    }
    let mapforPane = viewModel?.leftView?.content[0]?.value;
    if (!mapforPane) {
      mapforPane = new Map<number, any>();
      mapforPane.set(0, this.listName);
    }

    const nameModal = {
      view: DualPaneView.multiInput,
      title: 'i18n.lists.downloadListModal.fileName',
      inputsPane: {
        items: [
          {
            type: MultiInputItems.input,
            label: 'i18n.lists.downloadListModal.fileName',
            placeHolder1: 'i18n.lists.downloadListModal.enterName',
            inputcleanup: trimSpaceTo60,
            maxChar: 60,
            errorMessage: 'i18n.lists.importListModal.unique',
            hasError: false,
            onInput: this.onNameInput,
            required: true,
            inputValue,
          },
        ],
        handleValueUpdate: handleInputOrRangeValueSave,
      },
      value: mapforPane,
      verifySaveButton: this.verifyNewName,
      overrideSaveOnNavigation: this.saveNewName,
    } as RightPaneInfo;
    updateRightPaneWithValueForMaps(viewModel, paneSelected, nameModal);
    return nameModal;
  };

  onNameInput = (item: InputLabelItems, value: any) => {
    item.inputValue = !!item.inputcleanup ? item.inputcleanup(value) : value;
    return item.inputValue;
  };

  verifyNewName = (vm: DualPaneViewModel) => {
    return !!vm?.rightView.value?.get(0);
  };

  saveNewName = (vm: DualPaneViewModel, item: BasicListItem, value: any) => {
    let subLabel = '';
    if (value) {
      subLabel = value.get(0);
    }
    item.value = value;
    item.subLabel = subLabel;
    return item;
  };

  buildListFormatsRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const listTypeModal = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.lists.downloadListModal.fileFormat',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
      },
      itemsObservable$: of([...this.downloadFormats]),
      handleItemSelection: this.formatSelection,
      overrideSaveOnNavigation: this.saveOverrideFormat,
    } as RightPaneInfo;
    listTypeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(listTypeModal);
    updateRightPaneWithValue(viewModel, paneSelected, listTypeModal);
    return listTypeModal;
  };

  formatSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    left.content = left.content.map(item => {
      if (item.selected) {
        return { ...item, subLabel: value, value: value };
      } else {
        return item;
      }
    });
    right.value = value;

    if (platform !== PlatformEnum.mobile) {
      if (this.nutritionFlag) {
        this.updateNutrition(left, value);
      }
      this.updateMasterListAssociation(left, value);
    }
  };

  updateNutrition = (left: LeftPaneInfo, value: any) => {
    const formatIncludesNutrition = value === 'CSV' || value === 'XLSX';
    const foundNutritionOption = !!this.downloadOptions.find(
      element => element.value === 'nutrition',
    );

    if (formatIncludesNutrition && !foundNutritionOption) {
      const masterListAssociationOption = this.downloadOptions.find(
        element => element.value === 'masterListAssociation',
      );
      const nutritionOption = {
        label: 'i18n.lists.downloadListModal.includeNutrition',
        value: 'nutrition',
        selected: false,
        type: MultiSelectItems.basic,
      };
      if (!!masterListAssociationOption) {
        const indexOfMasterListOption = this.downloadOptions.indexOf(
          masterListAssociationOption,
        );
        this.downloadOptions.splice(
          indexOfMasterListOption,
          0,
          nutritionOption,
        );
      } else {
        this.downloadOptions.push(nutritionOption);
      }
    } else if (foundNutritionOption && !formatIncludesNutrition) {
      this.downloadOptions = this.downloadOptions.filter(
        element => element.value !== 'nutrition',
      );
      this.filterOutDownloadOptionLeftPaneValueAndUpdateSubLabel(
        left,
        'nutrition',
      );
    }
  };

  updateMasterListAssociation = (left: LeftPaneInfo, value: any) => {
    const foundMasterListAssociationOption = !!this.downloadOptions.find(
      element => element.value === 'masterListAssociation',
    );
    const formatIncludesMasterListAssociation =
      value === 'CSV' || value === 'XLSX' || value === 'PDF';
    if (
      !foundMasterListAssociationOption &&
      formatIncludesMasterListAssociation
    ) {
      this.downloadOptions.push({
        label: 'i18n.lists.downloadListModal.masterListAssociation',
        value: 'masterListAssociation',
        selected: false,
        type: MultiSelectItems.basic,
      });
    } else if (
      foundMasterListAssociationOption &&
      !formatIncludesMasterListAssociation
    ) {
      this.downloadOptions = this.downloadOptions.filter(
        element => element.value !== 'masterListAssociation',
      );
      this.filterOutDownloadOptionLeftPaneValueAndUpdateSubLabel(
        left,
        'masterListAssociation',
      );
    }
  };

  filterOutDownloadOptionLeftPaneValueAndUpdateSubLabel = (
    left: LeftPaneInfo,
    optionToFilter: string,
  ) => {
    if (!!left.content[2].value) {
      left.content[2].value = left.content[2].value.filter(
        element => element.value !== optionToFilter,
      );
      left.content[2].subLabel =
        left.content[2].value.length > 0
          ? this.translateService.instant(
              'i18n.lists.massDeleteModal.selected',
            ) + left.content[2].value.length
          : '';
    }
  };

  saveOverrideFormat = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    item.value = value;
    item.subLabel = value;
    if (this.nutritionFlag) {
      this.updateNutrition(vm.leftView, value);
    }
    this.updateMasterListAssociation(vm.leftView, value);
    return item;
  };

  buildListOptions = (viewModel: DualPaneViewModel, paneSelected: string) => {
    const excludeModal = {
      view: DualPaneView.multiSelectCheckbox,
      title: 'i18n.lists.downloadListModal.downloadOptions',
      verifySaveButton: (vm: any) => true,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
        searchTitle: '',
      },
      countPane: {
        primaryCount: 0,
        hideSelectAll: true,
        selectableText: '',
      },
      itemsObservable$: of([...this.downloadOptions]),
      handleItemSelection: this.excludeClassSelection,
      overrideSaveOnNavigation: this.saveOverrideExclude,
    } as RightPaneInfo;
    excludeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(excludeModal);
    updateRightPaneWithValue(viewModel, paneSelected, excludeModal);
    return excludeModal;
  };

  saveOverrideExclude = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    item.value = value;
    item.subLabel =
      value.length > 0
        ? this.translateService.instant('i18n.lists.massDeleteModal.selected') +
          value.length
        : '';
    return item;
  };

  excludeClassSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
    selectedCount: string,
  ) => {
    left.content[2].subLabel = value.length > 0 ? selectedCount : '';
    left.content[2].value = value;
    right.value = value;
  };

  listDownloadVerify = (vm: DualPaneViewModel) => {
    const nameInvalid =
      !vm.leftView.content[0]?.value || !vm.leftView.content[0]?.value?.get(0);
    const formatInvalid = !vm.leftView.content[1]?.value;

    return !(nameInvalid || formatInvalid);
  };

  submitModalValues = async (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();
    submitMap.set('name', vm.leftView.content[0]?.value.get(0));
    submitMap.set('format', vm.leftView.content[1]?.value);
    const options = vm.leftView.content[2].value;
    if (!!options) {
      options.forEach(element => {
        submitMap.set(element.value, true);
      });
    }

    submitMap.set('type', this.listTypeId);
    submitMap.set('id', this.listId);
    submitMap.set('listName', this.listName);
    submitMap.set('download', this.isDownload);
    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  async openModal(options: DownloadListModalOptions) {
    this.nutritionFlag = await firstValueFrom(
      this.panAppState.feature$([
        FEATURES.split_global_download_list_nutritionals.name,
      ]),
    );
    this.listName = options.listName;
    this.listId = options.listId;
    this.listTypeId = options.listTypeId;
    this.isDownload = options.isDownload;
    this.createListDownloadVM();
    this.isModalOpen$.next(true);
  }

  closeModal = () => {
    this.isModalOpen$.next(false);
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      const submitMap = this.submit$.value;
      console.log('submitMap:\n', submitMap);
      const options = {
        fileName: submitMap.get('name'),
        format: submitMap.get('format'),
        includeProductPrices: !!submitMap.get('price'),
        includeProductNotes: !!submitMap.get('note'),
        includeProductType: !!submitMap.get('status'),
        includeProductStatus: !!submitMap.get('status'),
        includeMasterListAssociation: !!submitMap.get('masterListAssociation'),
        includeNutritionals:
          !!submitMap.get('nutrition') &&
          (submitMap.get('format') === 'CSV' ||
            submitMap.get('format') === 'XLSX'),
        listTypeId: submitMap.get('type'),
        listId: submitMap.get('id'),
        listName: submitMap.get('listName'),
        isDownload: submitMap.get('download'),
      } as DownloadListOptions;
      this.submitEvent.emit(options);
    }
  }
}
